import type { SurveyFeed } from './useSurveyResults';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useClient } from 'hooks/useClient';
import { config } from 'config';
import { RESULTS_STALE_MS, resultsKey } from './useSurveyResults';

/**
 * The responses feed: its page and filters, and the page itself.
 *
 * Server-paginated, unlike the library: useDataControl pages in memory, which is
 * right for a list of surveys and wrong for an unbounded response set. The page on
 * screen stays while the next loads (keepPreviousData), so the list does not
 * collapse and the pager keeps the keyboard focus it was given.
 */
export const useSurveyFeed = (surveyId: number) => {
  const statsClient = useClient(config.STATISTICS_URL);
  const [page, setPage] = useState(1);
  const [sentiment, setSentiment] = useState<string>('all');
  const [withComment, setWithComment] = useState(false);

  const query = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (sentiment !== 'all') query.set('sentiment', sentiment);
  if (withComment) query.set('withComment', 'true');

  const { data: feed, isFetching } = useQuery<SurveyFeed>({
    enabled: !!surveyId,
    queryKey: resultsKey(surveyId, 'feed', page, sentiment, withComment),
    queryFn: () => statsClient.get<SurveyFeed>(`/surveys/${surveyId}/responses?${query.toString()}`),
    staleTime: RESULTS_STALE_MS,
    placeholderData: keepPreviousData,
  });

  const setFilter = (next: string) => {
    setSentiment(next);
    setPage(1);
  };

  const toggleComments = () => {
    setWithComment((current) => !current);
    setPage(1);
  };

  return { feed, isFetching, page, setPage, sentiment, setFilter, withComment, toggleComments };
};
